import { DotsThreeVerticalIcon } from '@phosphor-icons/react';
import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns/format';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import colors from 'tailwindcss/colors';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Spinner } from '@/modules/common';
import { PaginatedTable } from '@/modules/common/components';
import { Routes } from '@/router/routes';

import { usePromptsByCategory, useSystemPromptContent } from '../../api';
import { SystemPromptResponse } from '../../types';

interface PromptsTableProps {
  categoryId: string;
  categoryName: string;
}

type PromptRow = SystemPromptResponse & { subrows: { content: string }[] };

const subRowColumns: ColumnDef<{ content: string }>[] = [
  {
    id: 'content',
    accessorKey: 'content',
    header: 'Content',
    cell: ({ getValue }) => (
      <pre className="whitespace-pre-wrap text-sm font-mono text-zinc-200 max-h-96 overflow-y-auto p-2 leading-relaxed">
        {getValue() as string}
      </pre>
    ),
  },
];

export function PromptsTable({ categoryId, categoryName }: PromptsTableProps) {
  const navigate = useNavigate();
  const { data, isFetching, ...tableProps } = usePromptsByCategory(categoryId);
  const { fetchContent } = useSystemPromptContent();

  const [contentCache, setContentCache] = useState<Record<string, string>>({});
  const [loadingIds, setLoadingIds] = useState<Record<string, boolean>>({});
  const [variantLoadingIds, setVariantLoadingIds] = useState<Record<string, boolean>>({});

  const tableData: PromptRow[] = data.map((item) => ({
    ...item,
    subrows: contentCache[item.id] ? [{ content: contentCache[item.id] }] : [],
  }));

  const ensureContent = async (promptId: string): Promise<string> => {
    if (contentCache[promptId]) return contentCache[promptId];
    const content = await fetchContent(promptId);
    setContentCache((prev) => ({ ...prev, [promptId]: content }));
    return content;
  };

  const handleCreateVariant = async (promptId: string) => {
    setVariantLoadingIds((prev) => ({ ...prev, [promptId]: true }));
    try {
      const content = await ensureContent(promptId);
      navigate(Routes.SystemPromptsNewPrompt, {
        state: { categoryId, categoryName, content },
      });
    } finally {
      setVariantLoadingIds((prev) => ({ ...prev, [promptId]: false }));
    }
  };

  const columns: ColumnDef<PromptRow>[] = [
    {
      size: 100,
      accessorKey: 'version',
      header: 'Version',
      cell: ({ row }) => row.getValue('version'),
    },
    {
      size: 300,
      accessorKey: 'notes',
      header: 'Notes',
      cell: ({ row }) => row.getValue('notes') || '—',
    },
    {
      size: 180,
      accessorKey: 'createdAt',
      header: 'Created At',
      cell: ({ row }) =>
        format(new Date(row.getValue('createdAt')), 'yyyy-MM-dd HH:mm'),
    },
    {
      id: 'showContent',
      size: 150,
      header: '',
      cell: ({ row }) => {
        const promptId = row.original.id;
        const isExpanded = row.getIsExpanded();
        const isLoading = !!loadingIds[promptId];

        const handleClick = async () => {
          if (isExpanded) {
            row.toggleExpanded();
            return;
          }
          setLoadingIds((prev) => ({ ...prev, [promptId]: true }));
          try {
            await ensureContent(promptId);
          } finally {
            setLoadingIds((prev) => ({ ...prev, [promptId]: false }));
          }
          row.toggleExpanded();
        };

        return (
          <Button
            variant="outline"
            size="sm"
            disabled={isLoading}
            onClick={handleClick}
          >
            {isLoading ? (
              <Spinner className="w-4 h-4" />
            ) : isExpanded ? (
              'Hide Content'
            ) : (
              'Show Content'
            )}
          </Button>
        );
      },
    },
    {
      id: 'actions',
      size: 40,
      enableHiding: false,
      cell: ({ row }) => {
        const promptId = row.original.id;
        const isLoading = !!variantLoadingIds[promptId];

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-8 w-8 p-0"
                disabled={isLoading}
              >
                <span className="sr-only">Open menu</span>
                {isLoading ? (
                  <Spinner className="w-4 h-4" />
                ) : (
                  <DotsThreeVerticalIcon color={colors.zinc[400]} />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => handleCreateVariant(promptId)}
              >
                Create Variant
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <PaginatedTable<PromptRow, { content: string }>
      data={tableData}
      columns={columns}
      subRowColumns={subRowColumns}
      subRowKey="subrows"
      disabledColumnsIdsToClick={['version', 'notes', 'createdAt', 'showContent', 'actions']}
      isLoading={isFetching}
      emptyDescription="No prompts found for this category"
      {...tableProps}
    />
  );
}
